import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { STATUS_ORDER, STATUS_LABELS, LIMITS } from '@launchpad/shared';
import { useAppStore } from '../../store/useAppStore.js';
import { useToastStore } from '../../store/useToastStore.js';
import { updateStatus, updateOrder, batchReorder } from '../../api/client.js';
import { Column } from './Column.jsx';
import { ApplicationCard } from './ApplicationCard.jsx';

export function KanbanBoard({ applications: filteredApps, onCardClick, onContextMenu, statusOrder = STATUS_ORDER, statusLabels = STATUS_LABELS }) {
  const allApplications = useAppStore((s) => s.applications);
  const updateApplicationLocal = useAppStore((s) => s.updateApplicationLocal);
  const setDragging = useAppStore((s) => s.setDragging);
  const addToast = useToastStore((s) => s.addToast);

  // Custom collision detection: prefer pointerWithin (catches empty columns) then closestCorners
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  }, []);

  const [activeId, setActiveId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const columnApps = useMemo(() => {
    const map = {};
    for (const status of statusOrder) map[status] = [];
    for (const app of (filteredApps || allApplications)) {
      if (map[app.status]) map[app.status].push(app);
    }
    return map;
  }, [filteredApps, allApplications, statusOrder]);

  const activeApp = activeId ? allApplications.find((a) => a.appId === activeId) : null;

  const handleDragStart = useCallback(({ active }) => {
    setActiveId(active.id);
    setDragging(active.id, true);
  }, [setDragging]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    setActiveId(null);
    setDragging(active.id, false);

    if (!over || reordering) return;

    const app = allApplications.find((a) => a.appId === active.id);
    if (!app) return;

    // Determine target status
    const targetStatus = over.data?.current?.status || over.id;
    if (!statusOrder.includes(targetStatus) && !statusOrder.includes(app.status)) return;
    const isStatusChange = targetStatus !== app.status && statusOrder.includes(targetStatus);

    // Determine target column apps
    const targetApps = [...(columnApps[isStatusChange ? targetStatus : app.status] || [])]
      .filter((a) => a.appId !== active.id)
      .sort((a, b) => a.columnOrder - b.columnOrder);

    // Calculate new columnOrder
    const overIndex = targetApps.findIndex((a) => a.appId === over.id);
    let newOrder;

    if (targetApps.length === 0) {
      newOrder = LIMITS.COLUMN_ORDER_SPACING;
    } else if (overIndex === -1 || overIndex === targetApps.length - 1) {
      // Drop at bottom
      newOrder = targetApps[targetApps.length - 1].columnOrder + LIMITS.COLUMN_ORDER_SPACING;
    } else if (overIndex === 0) {
      // Drop at top
      newOrder = targetApps[0].columnOrder - LIMITS.COLUMN_ORDER_SPACING;
    } else {
      // Drop between
      const before = targetApps[overIndex - 1].columnOrder;
      const after = targetApps[overIndex].columnOrder;
      newOrder = (before + after) / 2;

      // Check if batch-reorder needed
      if (Math.abs(after - before) < LIMITS.COLUMN_ORDER_MIN_SPACING) {
        await triggerBatchReorder(isStatusChange ? targetStatus : app.status, app.appId, newOrder);
        return;
      }
    }

    // Optimistic update
    const changes = { columnOrder: newOrder };
    if (isStatusChange) changes.status = targetStatus;
    updateApplicationLocal(app.appId, changes);

    // Persist
    try {
      if (isStatusChange) {
        await updateStatus(app.appId, targetStatus, newOrder);
      } else {
        await updateOrder(app.appId, newOrder);
      }
    } catch {
      // Rollback
      updateApplicationLocal(app.appId, { status: app.status, columnOrder: app.columnOrder });
      addToast('Failed to update. Change reverted.');
    }
  }, [allApplications, columnApps, reordering, updateApplicationLocal, setDragging, statusOrder]);

  async function triggerBatchReorder(status, movedAppId, movedOrder) {
    setReordering(true);

    const colApps = allApplications
      .filter((a) => a.status === status && a.appId !== movedAppId)
      .sort((a, b) => a.columnOrder - b.columnOrder);

    // Insert moved app at correct position
    const allApps = [...colApps, { appId: movedAppId, columnOrder: movedOrder }]
      .sort((a, b) => a.columnOrder - b.columnOrder);

    // Recalculate to integer increments
    const items = allApps.map((a, i) => ({
      appId: a.appId,
      columnOrder: (i + 1) * LIMITS.COLUMN_ORDER_SPACING,
    }));

    // Snapshot only affected items' previous columnOrder for targeted rollback
    const prevOrders = new Map(
      items.map(({ appId }) => {
        const app = allApplications.find((a) => a.appId === appId);
        return [appId, app?.columnOrder];
      })
    );

    // Optimistic update
    for (const item of items) {
      updateApplicationLocal(item.appId, { columnOrder: item.columnOrder });
    }

    try {
      await batchReorder(items);
    } catch {
      // Revert only the affected items' columnOrder
      for (const [appId, order] of prevOrders) {
        if (order !== undefined) updateApplicationLocal(appId, { columnOrder: order });
      }
      addToast('Reorder failed. Changes reverted.');
    } finally {
      setReordering(false);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements: {
          onDragStart({ active }) { return `Picked up ${active.id}`; },
          onDragOver({ active, over }) { return over ? `Moving over ${over.id}` : ''; },
          onDragEnd({ active, over }) { return over ? `Dropped ${active.id} on ${over.id}` : `Cancelled`; },
          onDragCancel({ active }) { return `Cancelled dragging ${active.id}`; },
        },
      }}
    >
      <div className={`flex h-full gap-4 overflow-x-auto p-4 ${reordering ? 'pointer-events-none opacity-75' : ''}`}>
        {statusOrder.map((status) => (
          <Column
            key={status}
            status={status}
            label={statusLabels[status]}
            apps={columnApps[status]}
            onCardClick={onCardClick}
            onContextMenu={onContextMenu}
          />
        ))}
      </div>

      <DragOverlay>
        {activeApp ? <ApplicationCard app={activeApp} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
